import { type EventShapes } from '@editor/global/events';
import { DispatchEvent } from '@editor/types/events';
import { Subject } from 'rxjs';

export interface ResponseWithError {
  error: string;
}

export type EventMetadata = {
  collab: {
    /**
     * If true, the event will not be handled by the yjs outgoing handlers - this is very rarely used as normally you want to use
     * some sort of untracked events instead - whereas this is for the time where you want to send the event _from_ yjs
     * but not have it infinitely loop back into yjs
     */
    ignore?: boolean;
  };
};

export type EventBusData<T = unknown> = {
  key: DispatchEvent;
  val?: T;
  metadata?: EventMetadata;
};
export type EventBus = Subject<EventBusData>;
export type EventPayload<K extends DispatchEvent> = EventShapes[K];
export type Dispatcher = <K extends DispatchEvent>(
  key: K,
  value?: EventPayload<K>,
  metadata?: EventMetadata
) => void;

export enum WorkingMode {
  'Default' = 'default', // one is working on a design
  'Asset' = 'asset', // one is working on an asset
  'Mockup' = 'mockup', // one is working on a mockup
  'MockupTemplate' = 'mockupTemplate', // one is working on a mockup template
}

export type GenericCmsResponse<T = Record<string, never>> =
  | ({ success: true; error: never } & T)
  | {
      success: false;
      error: string;
    };

export interface ApiErrorResponse {
  success: false;
  error: string;
  errorMessage?: string;
  issues?: Record<string, unknown>[]; // an issue object created by zod
  errorObject?: Error; // an error if thrown on the client
  id?: string;
}

export type GenericApiResponse<
  T = Record<string, never>,
  U = Record<never, never>,
> = ({ success: true; error: never } & T) | (ApiErrorResponse & U);

export type ApiPaginatedResponse<T> =
  | ({
      take: number;
      skip: number;
      cursor?: string | null;
      nextCursor?: string;
    } & T)
  | ApiErrorResponse;

export enum UploadError {
  PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE',
  DIMENSIONS_TOO_LARGE = 'DIMENSIONS_TOO_LARGE',
  FILE_COUNT_TOO_LARGE = 'FILE_COUNT_TOO_LARGE',
  UNSUPPORTED_FILE_TYPE = 'UNSUPPORTED_FILE_TYPE',
  UPLOAD_SPACE_LIMIT_REACHED = 'UPLOAD_SPACE_LIMIT_REACHED',
  SVG_CONTAINS_EMBEDDED_DATA = 'SVG_CONTAINS_EMBEDDED_DATA',
  UNKNOWN = 'UNKNOWN',
}

// These are current used for YJS and need to align with whats set on the "live" server
export enum ResourceType {
  Design = 'designs',
  Presentation = 'presentations',
  Workspace = 'workspaces',

  // this is a temporary resource type for the asset library - it should not connect to yjs etc, but just gives us our own undo/redo stack
  LibraryAsset = 'library-assets',
}

export type StoreSelector<T> = {
  [K in keyof T]: (state: T) => T[K];
};

export enum Align {
  left = 'left',
  center = 'center',
  middle = 'middle',
  bottom = 'bottom',
  top = 'top',
  right = 'right',
}
